import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  FileText,
  Filter,
  FolderOpen,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
  UploadCloud,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import epcProjectService, {
  type EpcProjectAttachment,
  type EpcProjectOrgUnit,
} from '../../../../services/epcProjectService';
import { fileService } from '../../../../services/fileService';
import { useEpcProjectDetail } from '../EpcProjectDetailContext';
import { twMerge } from 'tailwind-merge';
import { DOC_TAGS, buildFileName, parseFileName, tagMeta } from '../../shared/documentDictionary';

/* ── Validation constants ───────────────────────────────────────────────── */
const NAME_MIN = 2;
const NAME_MAX = 150;
const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB

/* ── Display-name validation ─────────────────────────────────────────────
 *  Returns a single inline error string (or null when valid).
 */
function validateDisplayName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Display name is required.';
  if (trimmed.length < NAME_MIN) return `Use at least ${NAME_MIN} characters.`;
  if (trimmed.length > NAME_MAX) return `Keep it under ${NAME_MAX} characters.`;
  return null;
}

/* ── Tag badge (for file list rows) ─────────────────────────────────────── */
function TagBadge({ tag }: { tag: string }) {
  const meta = tagMeta(tag);
  if (!meta) return null;
  return (
    <span
      className={twMerge(
        'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ring-1',
        meta.color
      )}
    >
      {meta.short}
    </span>
  );
}

/* ── Upload panel (inline card, appears when a file is staged) ───────────── */
function UploadPanel({
  file,
  name,
  tag,
  saving,
  accent,
  duplicateWarning,
  onNameChange,
  onTagChange,
  onConfirm,
  onCancel,
}: {
  file: File;
  name: string;
  tag: string;
  saving: boolean;
  accent: 'project' | 'dept';
  duplicateWarning: string | null;
  onNameChange: (v: string) => void;
  onTagChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const accentSelected =
    accent === 'project'
      ? 'border-[#1F5A47] bg-[#1F5A47] text-white shadow-sm'
      : 'border-[#C9A961] bg-[#C9A961] text-white shadow-sm';

  const nameError = validateDisplayName(name);
  const charCount = name.trim().length;
  const overLimit = charCount > NAME_MAX;
  const canSubmit = !saving && nameError === null;

  const fileSizeKb = (file.size / 1024).toFixed(0);
  const fileSizeLabel = file.size >= 1024 * 1024
    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
    : `${fileSizeKb} KB`;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canSubmit) onConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (!saving) onCancel();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="mb-4 overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.16)] bg-[#FFFEFB] shadow-[0_1px_2px_rgba(11,59,46,0.04),0_16px_40px_-24px_rgba(11,59,46,0.20)]"
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-[rgba(11,59,46,0.10)] bg-[#F8F2E2] px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FCFBF6] shadow-sm ring-1 ring-[rgba(11,59,46,0.14)]">
          <FileText className="h-4 w-4 text-[#1F5A47]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-[#2A1F17]">{file.name}</p>
          <p className="text-[10px] text-[#7C8A82]">{fileSizeLabel}</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          aria-label="Cancel upload"
          className="rounded-lg p-1.5 text-[#7C8A82] transition hover:bg-[rgba(11,59,46,0.06)] hover:text-[#0B3B2E] disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Display name */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[#7C8A82]">
              Display name
            </label>
            <span className={twMerge('text-[10px] font-medium tabular-nums', overLimit ? 'text-[#9F1239]' : 'text-[#7C8A82]')}>
              {charCount}/{NAME_MAX}
            </span>
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            autoFocus
            aria-invalid={nameError !== null}
            className={twMerge(
              'w-full rounded-lg border bg-[#fcfaf3] px-3 py-2 text-sm text-[#2A1F17] shadow-sm transition focus:bg-white focus:outline-none focus:ring-2',
              nameError
                ? 'border-[rgba(159,18,57,0.5)] focus:border-[#9F1239] focus:ring-[rgba(159,18,57,0.18)]'
                : 'border-[rgba(11,59,46,0.28)] focus:border-[#0B3B2E] focus:ring-[rgba(166,206,57,0.25)]'
            )}
            placeholder="Enter a display name…"
          />
          {nameError && (
            <p className="mt-1.5 text-[11px] font-medium text-[#9F1239]">{nameError}</p>
          )}
          {!nameError && duplicateWarning && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-[#8A5A08]">
              <TriangleAlert className="h-3 w-3 shrink-0" />
              {duplicateWarning}
            </p>
          )}
        </div>

        {/* Category tag selector */}
        <div>
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#7C8A82]">
            Document category
          </label>
          <div className="flex flex-wrap gap-1.5">
            {DOC_TAGS.map((t) => {
              const selected = tag === t.label;
              return (
                <button
                  key={t.label}
                  type="button"
                  disabled={saving}
                  onClick={() => onTagChange(selected ? '' : t.label)}
                  className={twMerge(
                    'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all',
                    selected
                      ? accentSelected
                      : 'border-[rgba(11,59,46,0.18)] bg-[#FCFBF6] text-[#2A1F17] hover:border-[rgba(11,59,46,0.32)] hover:bg-[#F8F2E2]'
                  )}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          {!tag && (
            <p className="mt-1.5 text-[10px] text-[#7C8A82]">
              Select a category or leave blank — you can skip this.
            </p>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-2 border-t border-[rgba(11,59,46,0.10)] bg-[#F8F2E2]/70 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-[#2A1F17] transition hover:bg-[rgba(11,59,46,0.06)] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 rounded-lg bg-[#264f3c] px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2d5c47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(166,206,57,0.4)] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <>
              <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Uploading…
            </>
          ) : (
            <>
              <UploadCloud className="h-3.5 w-3.5" />
              Upload file
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

/* ── Types ───────────────────────────────────────────────────────────────── */
function orgUnitKey(u: EpcProjectOrgUnit): string {
  return String(u._id ?? u.id ?? u.name);
}

type StagedFile = { file: File; name: string; tag: string };

const EASE = [0.22, 1, 0.36, 1] as const;

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.02 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

/* ── Main component ──────────────────────────────────────────────────────── */
export default function EpcProjectDocumentsTab() {
  const { id: projectId, project, reload, canUpdate, myCap } = useEpcProjectDetail();
  const isGovernance = myCap === 'governance';

  const [orgUnits, setOrgUnits] = useState<EpcProjectOrgUnit[]>([]);
  const [search, setSearch] = useState('');
  const [deptFilterId, setDeptFilterId] = useState('');
  const [showInactiveDepts, setShowInactiveDepts] = useState(false);

  const projectFileRef = useRef<HTMLInputElement>(null);
  const [projectStaged, setProjectStaged] = useState<StagedFile | null>(null);
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectDeleting, setProjectDeleting] = useState<string | null>(null);
  const [projectDragging, setProjectDragging] = useState(false);

  const deptFileRef = useRef<HTMLInputElement>(null);
  const [activeDeptId, setActiveDeptId] = useState<string | null>(null);
  const [deptStaged, setDeptStaged] = useState<StagedFile | null>(null);
  const [deptSaving, setDeptSaving] = useState(false);
  const [deptDeleting, setDeptDeleting] = useState<string | null>(null);
  const [deptDragId, setDeptDragId] = useState<string | null>(null);

  const loadOrgUnits = useCallback(async () => {
    try {
      const rows = await epcProjectService.listOrgUnits(projectId);
      setOrgUnits(rows);
    } catch {
      setOrgUnits([]);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await epcProjectService.listOrgUnits(projectId);
        if (!cancelled) setOrgUnits(rows);
      } catch {
        if (!cancelled) setOrgUnits([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, loadOrgUnits]);

  const openFile = (fileKey: string) => {
    void (async () => {
      try {
        const url = await fileService.getSignedUrl(fileKey);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        toast.error('Could not open document');
      }
    })();
  };

  const allAttachments = project?.epcAttachments ?? [];
  const toPayload = (atts: EpcProjectAttachment[]) =>
    atts.map(({ fileName, fileKey }) => ({ fileName, fileKey }));

  const pushAttachment = async (att: EpcProjectAttachment) => {
    await epcProjectService.update(projectId, { epcAttachments: toPayload([...allAttachments, att]) });
    await reload();
  };

  const removeAttachment = async (fileKey: string) => {
    await epcProjectService.update(projectId, {
      epcAttachments: toPayload(allAttachments.filter((a) => a.fileKey !== fileKey)),
    });
    await reload();
  };

  /* ── shared validation: size guard at file-select time ── */
  const guardFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`File is too large — keep it under 25 MB (this one is ${(file.size / (1024 * 1024)).toFixed(1)} MB).`);
      return false;
    }
    return true;
  };

  /* ── project-level ── */
  const stageProjectFile = (file: File) => {
    if (!guardFileSize(file)) return;
    setProjectStaged({ file, name: file.name, tag: '' });
  };

  const handleProjectFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    stageProjectFile(file);
    e.target.value = '';
  };

  const confirmProjectUpload = async () => {
    if (!projectStaged) return;
    if (validateDisplayName(projectStaged.name) !== null) return; // re-check guard
    setProjectSaving(true);
    try {
      const { key } = await fileService.uploadFile(projectStaged.file);
      await pushAttachment({
        fileName: buildFileName(projectStaged.name.trim(), projectStaged.tag),
        fileKey: key,
      });
      setProjectStaged(null);
      toast.success('File added to project repository');
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setProjectSaving(false);
    }
  };

  const deleteProjectFile = async (fileKey: string) => {
    setProjectDeleting(fileKey);
    try {
      await removeAttachment(fileKey);
      toast.success('File removed');
    } catch {
      toast.error('Could not remove file');
    } finally {
      setProjectDeleting(null);
    }
  };

  /* ── department-level ── */
  const stageDeptFile = (file: File, orgId: string) => {
    if (!guardFileSize(file)) return;
    setActiveDeptId(orgId);
    setDeptStaged({ file, name: file.name, tag: '' });
  };

  const handleDeptFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!guardFileSize(file)) { e.target.value = ''; return; }
    setDeptStaged({ file, name: file.name, tag: '' });
    e.target.value = '';
  };

  const triggerDeptUpload = (orgId: string) => {
    setActiveDeptId(orgId);
    setDeptStaged(null);
    deptFileRef.current?.click();
  };

  const cancelDeptUpload = () => { setDeptStaged(null); setActiveDeptId(null); };

  const confirmDeptUpload = async () => {
    if (!deptStaged || !activeDeptId) return;
    if (validateDisplayName(deptStaged.name) !== null) return; // re-check guard
    setDeptSaving(true);
    try {
      const { key } = await fileService.uploadFile(deptStaged.file);
      await pushAttachment({
        fileName: buildFileName(deptStaged.name.trim(), deptStaged.tag, activeDeptId),
        fileKey: key,
      });
      setDeptStaged(null);
      setActiveDeptId(null);
      toast.success('File added to department workspace');
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setDeptSaving(false);
    }
  };

  const deleteDeptFile = async (fileKey: string) => {
    setDeptDeleting(fileKey);
    try {
      await removeAttachment(fileKey);
      toast.success('File removed');
    } catch {
      toast.error('Could not remove file');
    } finally {
      setDeptDeleting(null);
    }
  };

  /* ── drag-and-drop helpers ── */
  const extractDropFile = (e: React.DragEvent): File | null => {
    const file = e.dataTransfer?.files?.[0];
    return file ?? null;
  };

  const onProjectDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setProjectDragging(false);
    if (!canUpdate || projectStaged) return;
    const file = extractDropFile(e);
    if (file) stageProjectFile(file);
  };

  const onDeptDrop = (e: React.DragEvent, orgId: string) => {
    e.preventDefault();
    setDeptDragId(null);
    if (!canUpdate) return;
    const file = extractDropFile(e);
    if (file) stageDeptFile(file, orgId);
  };

  /* ── derived data ── */
  const q = search.trim().toLowerCase();

  const parsedAttachments = useMemo(
    () => allAttachments.map((a) => ({ ...a, ...parseFileName(a.fileName) })),
    [allAttachments]
  );

  const projectFiles = useMemo(
    () => parsedAttachments.filter((a) => !a.deptId),
    [parsedAttachments]
  );

  /* ── duplicate-name detection (non-blocking warning) ── */
  const projectDuplicateWarning = useMemo(() => {
    if (!projectStaged) return null;
    const trimmed = projectStaged.name.trim();
    if (trimmed.length < NAME_MIN) return null;
    const clash = projectFiles.some(
      (a) => a.displayName.toLowerCase() === trimmed.toLowerCase() && (a.tag ?? '') === projectStaged.tag
    );
    return clash ? `A document named “${trimmed}” already exists here.` : null;
  }, [projectStaged, projectFiles]);

  const deptDuplicateWarning = useMemo(() => {
    if (!deptStaged || !activeDeptId) return null;
    const trimmed = deptStaged.name.trim();
    if (trimmed.length < NAME_MIN) return null;
    const clash = parsedAttachments.some(
      (a) => a.deptId === activeDeptId && a.displayName.toLowerCase() === trimmed.toLowerCase() && (a.tag ?? '') === deptStaged.tag
    );
    return clash ? `A document named “${trimmed}” already exists here.` : null;
  }, [deptStaged, activeDeptId, parsedAttachments]);

  const filteredProjectFiles = useMemo(() => {
    return projectFiles.filter(
      (a) => !q || a.displayName.toLowerCase().includes(q) || (a.tag ?? '').toLowerCase().includes(q)
    );
  }, [projectFiles, q]);

  const filteredDepartments = useMemo(() => {
    return orgUnits.filter((u) => {
      if (!showInactiveDepts && u.inactive) return false;
      if (deptFilterId && orgUnitKey(u) !== deptFilterId) return false;
      if (!q) return true;
      return (u.name ?? '').toLowerCase().includes(q) || (u.code ?? '').toLowerCase().includes(q);
    });
  }, [orgUnits, q, deptFilterId, showInactiveDepts]);

  const activeFilterCount =
    (q ? 1 : 0) + (deptFilterId ? 1 : 0) + (showInactiveDepts ? 1 : 0);

  const clearFilters = () => { setSearch(''); setDeptFilterId(''); setShowInactiveDepts(false); };

  if (!project) return null;

  return (
    <div className="mx-auto max-w-7xl font-[family-name:var(--font-outfit)]">
      <input ref={projectFileRef} type="file" className="hidden" onChange={handleProjectFileSelect} />
      <input ref={deptFileRef} type="file" className="hidden" onChange={handleDeptFileSelect} />

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="mb-5"
      >
        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-[#C9A961]">Project repository</p>
        <h2 className="text-lg font-bold tracking-tight text-[#0B3B2E] sm:text-xl">Document repository</h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#7C8A82]">
          {isGovernance
            ? 'Upload and manage project-level DPR files and department workspace documents. Filter by name, category, or department.'
            : 'Browse and upload department workspace documents for your discipline area.'}
        </p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.16)] bg-[#FFFEFB] shadow-[0_1px_2px_rgba(11,59,46,0.04),0_16px_40px_-24px_rgba(11,59,46,0.20)]"
      >
        {/* ── Filter bar ── */}
        <div className="border-b border-[rgba(11,59,46,0.12)] bg-[#F8F2E2] px-4 py-3 sm:px-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#7C8A82]">
              <Filter className="h-3.5 w-3.5 text-[#339274]" />
              Filters
            </span>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#0B3B2E] transition hover:bg-[rgba(11,59,46,0.06)] sm:ml-0"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-12 lg:gap-3">
            <label className="lg:col-span-8">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#7C8A82]">Search</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#7C8A82]" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="File name or category…"
                  className="w-full rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] py-2 pl-8 pr-2.5 text-sm text-[#2A1F17] shadow-sm transition focus:border-[#0B3B2E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.25)]"
                />
              </div>
            </label>
            <div className="lg:col-span-4">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#7C8A82]">Department</span>
              <select
                value={deptFilterId}
                onChange={(e) => setDeptFilterId(e.target.value)}
                className="w-full rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-2 text-sm text-[#2A1F17] shadow-sm transition focus:border-[#0B3B2E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[rgba(166,206,57,0.25)]"
              >
                <option value="">All departments</option>
                {orgUnits.map((u) => (
                  <option key={orgUnitKey(u)} value={orgUnitKey(u)}>
                    {u.name}{u.inactive ? ' (inactive)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs font-medium text-[#2A1F17]">
            <input
              type="checkbox"
              checked={showInactiveDepts}
              onChange={(e) => setShowInactiveDepts(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-[rgba(11,59,46,0.32)] text-[#1F5A47] focus:ring-[#339274]"
            />
            Include inactive departments
          </label>
        </div>

        {/* ── Content: two-column for governance, single-column for dept roles ── */}
        <div className={twMerge('grid gap-0', isGovernance && 'lg:grid-cols-2')}>

          {/* ── Project-level files — governance only ── */}
          {isGovernance ? (
          <motion.section variants={itemVariants} className="border-b border-[rgba(11,59,46,0.12)] p-4 sm:p-5 lg:border-b-0 lg:border-r">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1F5A47]">Shared repository</p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <FolderOpen className="h-5 w-5 text-[#1F5A47]" />
              <h3 className="text-sm font-bold text-[#0B3B2E]">Project-level</h3>
              <span className="rounded-full bg-[rgba(31,90,71,0.10)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1F5A47] ring-1 ring-[rgba(31,90,71,0.25)]">
                DPR shared
              </span>
              <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[#F8F2E2] px-1.5 font-mono text-[11px] tabular-nums text-[#7C8A82] ring-1 ring-[rgba(11,59,46,0.10)]">
                {filteredProjectFiles.length}
              </span>
              {canUpdate && !projectStaged && (
                <button
                  type="button"
                  onClick={() => projectFileRef.current?.click()}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[rgba(31,90,71,0.25)] bg-[rgba(31,90,71,0.08)] px-2.5 py-1.5 text-[11px] font-semibold text-[#1F5A47] shadow-sm transition hover:bg-[rgba(31,90,71,0.14)]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add file
                </button>
              )}
            </div>

            {(
              <>
                {projectStaged && (
                  <UploadPanel
                    file={projectStaged.file}
                    name={projectStaged.name}
                    tag={projectStaged.tag}
                    saving={projectSaving}
                    accent="project"
                    duplicateWarning={projectDuplicateWarning}
                    onNameChange={(v) => setProjectStaged((s) => (s ? { ...s, name: v } : null))}
                    onTagChange={(v) => setProjectStaged((s) => (s ? { ...s, tag: v } : null))}
                    onConfirm={() => void confirmProjectUpload()}
                    onCancel={() => setProjectStaged(null)}
                  />
                )}

                {filteredProjectFiles.length > 0 ? (
                  <ul className="space-y-2">
                    {filteredProjectFiles.map((att, i) => (
                      <motion.li
                        key={`${att.fileKey}-${i}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03, duration: 0.4, ease: EASE }}
                      >
                        <div className="group flex w-full items-center gap-2.5 rounded-xl border border-[rgba(11,59,46,0.12)] bg-[#FCFBF6] px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:border-[rgba(31,90,71,0.3)] hover:shadow-[0_8px_24px_-16px_rgba(11,59,46,0.30)]">
                          <FileText className="h-4 w-4 shrink-0 text-[#1F5A47]" />
                          {att.tag && <TagBadge tag={att.tag} />}
                          <button
                            type="button"
                            className="min-w-0 flex-1 truncate text-left text-sm font-medium text-[#2A1F17] transition hover:text-[#0B3B2E]"
                            onClick={() => openFile(att.fileKey)}
                          >
                            {att.displayName}
                          </button>
                          {canUpdate && (
                            <button
                              type="button"
                              title="Remove"
                              disabled={projectDeleting === att.fileKey}
                              onClick={() => void deleteProjectFile(att.fileKey)}
                              className="shrink-0 rounded-lg p-1 text-[#7C8A82] transition hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239] disabled:opacity-40"
                            >
                              {projectDeleting === att.fileKey ? (
                                <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[rgba(11,59,46,0.2)] border-t-[#9F1239]" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                          <button type="button" onClick={() => openFile(att.fileKey)} className="shrink-0" aria-label="Open document">
                            <ArrowRight className="h-4 w-4 text-[#C0C9C3] transition group-hover:text-[#1F5A47]" />
                          </button>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                ) : !projectStaged ? (
                  <button
                    type="button"
                    onClick={() => canUpdate && projectFileRef.current?.click()}
                    onDragOver={(e) => { if (canUpdate) { e.preventDefault(); setProjectDragging(true); } }}
                    onDragLeave={() => setProjectDragging(false)}
                    onDrop={onProjectDrop}
                    className={twMerge(
                      'flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed py-10 text-center transition',
                      canUpdate
                        ? projectDragging
                          ? 'cursor-pointer border-[#1F5A47] bg-[rgba(31,90,71,0.10)] ring-2 ring-[rgba(166,206,57,0.3)]'
                          : 'cursor-pointer border-[rgba(31,90,71,0.3)] bg-[#FCFBF6] hover:bg-[#F8F2E2]'
                        : 'cursor-default border-[rgba(11,59,46,0.16)] bg-[#FCFBF6]'
                    )}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[rgba(31,90,71,0.08)] ring-1 ring-[rgba(31,90,71,0.18)]">
                      <UploadCloud className={twMerge('h-6 w-6', canUpdate ? 'text-[#1F5A47]' : 'text-[#7C8A82]')} />
                    </span>
                    <span className={twMerge('text-sm font-medium', canUpdate ? 'text-[#1F5A47]' : 'text-[#7C8A82]')}>
                      {projectFiles.length === 0
                        ? canUpdate ? 'No project documents yet — click or drop a file to upload' : 'No project documents uploaded yet.'
                        : 'No files match the current filters.'}
                    </span>
                    {canUpdate && projectFiles.length === 0 && (
                      <span className="text-xs text-[#7C8A82]">Any file type · up to 25 MB</span>
                    )}
                  </button>
                ) : null}
              </>
            )}
          </motion.section>
          ) : null}

          {/* ── Department workspaces (champagne accent) ── */}
          <motion.section variants={itemVariants} className="p-4 sm:p-5">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A961]">Discipline files</p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Building2 className="h-5 w-5 text-[#8A5A08]" />
              <h3 className="text-sm font-bold text-[#0B3B2E]">Department workspaces</h3>
              <span className="rounded-full bg-[rgba(201,169,97,0.16)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A5A08] ring-1 ring-[rgba(201,169,97,0.45)]">
                Org units
              </span>
              <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[#F8F2E2] px-1.5 font-mono text-[11px] tabular-nums text-[#7C8A82] ring-1 ring-[rgba(11,59,46,0.10)]">
                {filteredDepartments.length}
              </span>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-[#7C8A82]">
              Upload discipline-specific documents per department. Manage structure under{' '}
              <Link to="../team" className="font-semibold text-[#1F5A47] underline-offset-2 hover:underline">
                Team hierarchy
              </Link>
              .
            </p>

            {filteredDepartments.length > 0 ? (
              <ul className="max-h-[min(600px,65vh)] space-y-3 overflow-y-auto pr-1">
                {filteredDepartments.map((u) => {
                  const key = orgUnitKey(u);
                  const isActive = activeDeptId === key;
                  const deptFiles = parsedAttachments.filter((a) => a.deptId === key);
                  const canAdd = canUpdate && !u.inactive;
                  const isDragging = deptDragId === key;

                  return (
                    <li
                      key={key}
                      onDragOver={(e) => { if (canAdd) { e.preventDefault(); setDeptDragId(key); } }}
                      onDragLeave={() => setDeptDragId((d) => (d === key ? null : d))}
                      onDrop={(e) => onDeptDrop(e, key)}
                      className={twMerge(
                        'rounded-2xl border bg-[#FFFEFB] shadow-[0_1px_2px_rgba(11,59,46,0.04),0_12px_32px_-24px_rgba(11,59,46,0.18)] transition',
                        isDragging
                          ? 'border-[#C9A961] ring-2 ring-[rgba(201,169,97,0.35)]'
                          : 'border-[rgba(11,59,46,0.14)]'
                      )}
                    >
                      {/* Card header */}
                      <div className="flex items-start justify-between gap-2 px-3 pb-2 pt-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-[#0B3B2E]">{u.name}</p>
                          {u.code && <p className="font-mono text-[11px] text-[#7C8A82]">{u.code}</p>}
                          <p className="mt-0.5 text-[11px] text-[#7C8A82]">
                            {deptFiles.length} file{deptFiles.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {u.inactive && (
                            <span className="rounded bg-[rgba(124,138,130,0.14)] px-1.5 py-0.5 text-[10px] font-semibold text-[#5E6B63]">
                              Inactive
                            </span>
                          )}
                          {canAdd && !isActive && (
                            <button
                              type="button"
                              onClick={() => triggerDeptUpload(key)}
                              className="inline-flex items-center gap-1 rounded-lg border border-[rgba(201,169,97,0.45)] bg-[rgba(201,169,97,0.14)] px-2.5 py-1 text-[11px] font-semibold text-[#8A5A08] transition hover:bg-[rgba(201,169,97,0.22)]"
                            >
                              <Plus className="h-3 w-3" />
                              Add file
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Upload panel for this department */}
                      {isActive && deptStaged && (
                        <div className="px-3 pb-2">
                          <UploadPanel
                            file={deptStaged.file}
                            name={deptStaged.name}
                            tag={deptStaged.tag}
                            saving={deptSaving}
                            accent="dept"
                            duplicateWarning={deptDuplicateWarning}
                            onNameChange={(v) => setDeptStaged((s) => (s ? { ...s, name: v } : null))}
                            onTagChange={(v) => setDeptStaged((s) => (s ? { ...s, tag: v } : null))}
                            onConfirm={() => void confirmDeptUpload()}
                            onCancel={cancelDeptUpload}
                          />
                        </div>
                      )}

                      {/* Department file list */}
                      {deptFiles.length > 0 && (
                        <ul className="mx-3 mb-3 space-y-1.5">
                          {deptFiles.map((att, i) => (
                            <li
                              key={`${att.fileKey}-${i}`}
                              className="group flex items-center gap-2 rounded-lg border border-[rgba(11,59,46,0.12)] bg-[#FCFBF6] px-2.5 py-2 transition hover:border-[rgba(201,169,97,0.4)] hover:bg-[#F8F2E2]"
                            >
                              <FileText className="h-3.5 w-3.5 shrink-0 text-[#8A5A08]" />
                              {att.tag && <TagBadge tag={att.tag} />}
                              <button
                                type="button"
                                className="min-w-0 flex-1 truncate text-left text-xs font-medium text-[#2A1F17] transition hover:text-[#8A5A08]"
                                onClick={() => openFile(att.fileKey)}
                              >
                                {att.displayName}
                              </button>
                              {canUpdate && (
                                <button
                                  type="button"
                                  title="Remove"
                                  disabled={deptDeleting === att.fileKey}
                                  onClick={() => void deleteDeptFile(att.fileKey)}
                                  className="shrink-0 rounded p-0.5 text-[#7C8A82] transition hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239] disabled:opacity-40"
                                >
                                  {deptDeleting === att.fileKey ? (
                                    <span className="block h-3 w-3 animate-spin rounded-full border-2 border-[rgba(11,59,46,0.2)] border-t-[#9F1239]" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </button>
                              )}
                              <button type="button" onClick={() => openFile(att.fileKey)} className="shrink-0" aria-label="Open document">
                                <ArrowRight className="h-3.5 w-3.5 text-[#C0C9C3] transition group-hover:text-[#8A5A08]" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      {deptFiles.length === 0 && !isActive && (
                        <p className="px-3 pb-3 text-[11px] text-[#7C8A82]">
                          {canAdd
                            ? 'No files yet — use Add file or drop a file here.'
                            : 'No department files linked yet.'}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="rounded-2xl border border-dashed border-[rgba(11,59,46,0.16)] bg-[#FCFBF6] py-8 text-center text-sm text-[#7C8A82]">
                {orgUnits.length === 0 ? (
                  <>
                    No departments for this project.
                    <Link to="../team" className="mt-2 block text-sm font-semibold text-[#1F5A47] underline-offset-2 hover:underline">
                      Open Team hierarchy
                    </Link>
                  </>
                ) : (
                  'No departments match the current filters.'
                )}
              </div>
            )}
          </motion.section>
        </div>
      </motion.div>
    </div>
  );
}
